import { useMemo } from "react";
import { venues } from "../constants/venues";
import { Activity } from "../types/activity.types";
import { StorageItem } from "../types/storageItem.types";
import { Package, Product, ProductTypes } from "../types/product.types";
import { useFilterContext } from "../context/FilterContext";
import { useOrderContext } from "../context/OrderContext";
import { getFilteredStorageItems } from "../utils/filters";
import { FilterFoodTypeTag } from "../types/filter.types";

type UseCatalogDataArgs = {
    products: Array<Product | Package>;
    newProducts: Array<Product | Package>;
    items: StorageItem[];
    activities: Activity[];
};

const useCatalogData = ({ products, newProducts, items, activities }: UseCatalogDataArgs) => {
    const { filterData, basicFilter } = useFilterContext();
    const { orderData } = useOrderContext();

    const productItems: ProductTypes[] = useMemo(
        () =>
            products
                .filter((p) => p.type === "sales")
                .map((p) => ({
                    type: "sales",
                    data: { product: p },
                })),
        [products]
    );

    const newProductItems: ProductTypes[] = useMemo(
        () =>
            newProducts.map((p) => ({
                type: "new",
                data: { product: p },
            })),
        [newProducts]
    );

    const packageItems: ProductTypes[] = useMemo(
        () =>
            products
                .filter((p) => p.type === "package")
                .map((p) => ({
                    type: "package",
                    data: { product: p },
                })),
        [products]
    );

    const venueItems: ProductTypes[] = useMemo(
        () =>
            venues.map((v) => ({
                type: "venue",
                data: { ...v },
            })),
        []
    );

    const filteredStorage = useMemo(() => {
        if (basicFilter === "storage") {
            return getFilteredStorageItems(items, {
                ...filterData,
                typy_jidla: {
                    ...((filterData.typy_jidla ?? {}) as Record<FilterFoodTypeTag, boolean>),
                    storage: true,
                },
            }, orderData.selectedDate);
        }

        return getFilteredStorageItems(items, filterData, orderData.selectedDate);
    }, [basicFilter, filterData, items, orderData.selectedDate]);

    const extraItems: ProductTypes[] = useMemo(
        () =>
            filteredStorage.map((e) => ({
                type: "storage",
                data: { product: e },
            })),
        [filteredStorage]
    );

    const activityItems: ProductTypes[] = useMemo(
        () =>
            activities.map((e) => ({
                type: "activities",
                data: { product: e },
            })),
        [activities]
    );

    const filterDict = useMemo(
        () => ({
            sales: [...packageItems, ...productItems, ...extraItems] as ProductTypes[],
            new: newProductItems as ProductTypes[],
            package: packageItems as ProductTypes[],
            venue: venueItems as ProductTypes[],
            storage: extraItems as ProductTypes[],
            activities: activityItems as ProductTypes[],
        }),
        [activityItems, extraItems, newProductItems, packageItems, productItems, venueItems]
    );

    return {
        productItems,
        newProductItems,
        packageItems,
        venueItems,
        extraItems,
        activityItems,
        filterDict,
    };
};

export default useCatalogData;
